/* eslint-disable camelcase */
import router from '@/router'
import { eAuth } from './auth.endpoints'
import { VueCookieNext } from 'vue-cookie-next'
import { IUser } from './auth.interface'
import { Module } from 'vuex'
import client from '@/config/axios.config'
import _ from 'lodash'
import { identityFromToken } from '@/utils/token'
import {
  clearTokens,
  readAccessToken,
  restoreSession,
  storeAccessToken
} from './refresh'

// Identity used to be kept in these two, written by the page itself, and
// `fetchUser` fetched whichever account the `id` cookie named. Anything running
// in the browser could change them. They are read nowhere now; they are still
// cleared, because a browser that signed in before this change is carrying
// them and a stale `email` cookie is a stale claim about who is signed in.
const LEGACY_IDENTITY_COOKIES = ['email', 'id']

/** Who the current access token says we are, or null if it cannot say. */
const identityFromCookie = () => identityFromToken(readAccessToken())

const auth: Module<any, any> = {
  namespaced: true,
  state: {
    user: null as IUser | null
  },
  getters: {
    isAuthenticated: state => {
      return !!state.user
    },
    // The role from the signed token, not the `is_admin` boolean off a fetched
    // record. SMN-43 found those flags set inconsistently by the role-change
    // path, and the authorization layer decides on `user_account_type` alone --
    // so this is the value the server will actually judge the caller by.
    isAdmin: state => {
      return state?.user?.user_account_type === 'admin'
    }
  },
  mutations: {
    setUser(state, user: IUser | null) {
      state.user = user
    }
    // A `setToken` mutation used to sit here calling `context.dispatch(...)`.
    // A mutation is handed the state, not a context, so committing it would
    // have thrown -- and it dispatched 'getLoggedUser', an action that does not
    // exist. Nothing committed it: every caller dispatches the *action* of the
    // same name below. Removed rather than left as a trap.
  },
  actions: {
    /**
     * Establish who is signed in, from the access token (SMN-67, finding 3.9).
     *
     * This used to read an `id` cookie and GET whichever user it named, so the
     * portal's idea of the caller -- including their role -- came from a value
     * the client had written to itself. It also could not tell an expired token
     * from a live one, which since SMN-47 is the difference between a session
     * and a dead one.
     *
     * Everything the interface needs is in the token, and the token is the
     * thing the server signed.
     *
     * One request may be made, once per page load. Since SMN-68 the access
     * token lives in memory, so a reload starts with nothing -- but the
     * HttpOnly refresh cookie survives, and the browser may well still have a
     * perfectly good session. Without asking, every reload would land on the
     * login screen.
     */
    async fetchUser(context) {
      if (!readAccessToken()) await restoreSession()

      const claims = identityFromCookie()

      if (!claims) {
        context.commit('setUser', null)
        return
      }

      context.commit('setUser', {
        id: claims.id,
        user_email: claims.user_email,
        user_username: claims.user_username,
        user_account_type: claims.user_account_type
      })
    },
    async login(context, { email, password }) {
      const credentials = {
        user_email: email,
        user_password: password
      }
      const { data } = await client.post(eAuth.login, credentials)

      if (_.has(data, 'data.access_token')) {
        // Only the access token is taken from the body. The refresh token came
        // back in the same response, and is deliberately ignored: the server
        // has already put it in an HttpOnly cookie, and copying it anywhere
        // JavaScript can reach would undo the point of that (finding 3.27).
        context.dispatch('setToken', data.data.access_token)
        // Identity comes from the token rather than from the rest of the
        // response body, so there is one source for it and the value the guards
        // read is the value the server signed.
        await context.dispatch('fetchUser')

        const q = _.get(router, 'currentRoute.value.query.redirect')
        router.push({
          path: q ? `${q}` : '/'
        })
      } else {
        throw new Error(data.message)
      }
    },
    async logout(context) {
      const token = readAccessToken()
      if (token && token.length > 0) {
        try {
          await client.post(eAuth.logout)
        } catch (error) {
        } finally {
          context.dispatch('setToken', null)
          context.commit('setUser', null)
          router.push('/auth')
        }
      }
    },
    async forgotPassword(context, { email }) {
      const credentials = {
        user_email: email
      }
      return await client.post(eAuth.forgot, credentials)
    },
    /**
     * Hold or drop the access token.
     *
     * Only the access token: the refresh token is the server's HttpOnly cookie
     * and the portal neither receives nor stores it. `null` clears the session
     * -- the token in memory and the cookies older versions of the portal left
     * behind.
     */
    setToken: (context, token: string | null) => {
      LEGACY_IDENTITY_COOKIES.forEach(name => VueCookieNext.removeCookie(name))

      if (!token) {
        clearTokens()
        return
      }

      storeAccessToken(token)
    },
    verify(context, { token }) {
      return client.put(
        eAuth.verify,
        {},
        {
          params: {
            verification_token: token
          }
        }
      )
    },
    confirmPassword(context, { password, confirmPassword, token }) {
      const credentials = {
        user_password: password,
        confirm_user_password: confirmPassword,
        verification_token: token
      }
      return client.post(eAuth.newPassword, credentials)
    },
    resendVerification(context, { email }) {
      return client.post(eAuth.resendVerification, { user_email: email })
    },
    async changePassword(
      context,
      { old_user_password, user_password, confirm_new_user_password }
    ) {
      const credentials = {
        old_user_password,
        user_password,
        confirm_new_user_password
      }
      return await client.put(eAuth.changePassword, credentials)
    }
  }
}

export default auth
